import { TRPCClientErrorLike } from "@trpc/client";
import { UseTRPCQueryResult } from "@trpc/react-query/shared";

import type {
  ILlmUsageEventDebugArtifact,
  ILlmUsageEvent,
  ILlmUsageEventPage,
  ILlmUsagePricingTable,
} from "@monorepo/core";
import type { AppRouter } from "@monorepo/lambda-trpc";

import { ReactQueryOptions, RouterInputs, trpc } from "../utils/trpc";

type UpdateTaxonomyLlmPricingTableMutation = ReturnType<
  typeof trpc.taxonomy.updateLlmPricingTable.useMutation
>;

export function useTaxonomyLlmUsageEvents(
  options?: ReactQueryOptions["taxonomy"]["listLlmUsageEvents"],
): UseTRPCQueryResult<ILlmUsageEvent[], TRPCClientErrorLike<AppRouter>> {
  return trpc.taxonomy.listLlmUsageEvents.useQuery(
    {
      limit: 1000,
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      ...options,
    },
  );
}

export function useTaxonomyLlmPricingTable(): UseTRPCQueryResult<
  ILlmUsagePricingTable,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.getLlmPricingTable.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

export function useTaxonomyLlmUsageEventsPage(
  input: RouterInputs["taxonomy"]["listLlmUsageEventsPage"],
  options?: ReactQueryOptions["taxonomy"]["listLlmUsageEventsPage"],
): UseTRPCQueryResult<ILlmUsageEventPage, TRPCClientErrorLike<AppRouter>> {
  return trpc.taxonomy.listLlmUsageEventsPage.useQuery(input, {
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 60_000,
    ...options,
  });
}

export function useTaxonomyLlmUsageEventDebugArtifact(
  input: RouterInputs["taxonomy"]["getLlmUsageEventDebugArtifact"],
  options?: ReactQueryOptions["taxonomy"]["getLlmUsageEventDebugArtifact"],
): UseTRPCQueryResult<
  ILlmUsageEventDebugArtifact | undefined,
  TRPCClientErrorLike<AppRouter>
> {
  return trpc.taxonomy.getLlmUsageEventDebugArtifact.useQuery(input, {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useUpdateTaxonomyLlmPricingTable(): UpdateTaxonomyLlmPricingTableMutation {
  const utils = trpc.useUtils();

  return trpc.taxonomy.updateLlmPricingTable.useMutation({
    onSuccess: async () => {
      await utils.taxonomy.getLlmPricingTable.invalidate();
    },
  });
}
